import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  IconButton,
  TextField,
} from "@mui/material";
import { Close } from "@opengov/react-capital-assets";
import { FormattedMessage, useIntl } from "react-intl";
import { useState } from "react";

import { usePspOptions } from "../../../PspOptionsContext/usePspOptions";
import { useGetFormRequestTypes } from "../../hooks/useGetFormRequestTypes";
import { Routes } from "../../utils/routesUtils";

export type NewRequestDialogProps = {
  entitySubdomain: string;
  entityUuid: string;
  onClose: () => void;
};

export const NewRequestDialog = ({ entitySubdomain, entityUuid, onClose }: NewRequestDialogProps) => {
  const intl = useIntl();
  const pspOptions = usePspOptions();
  const [formTypeId, setFormTypeId] = useState<string | null>(null);

  const { data, isLoading } = useGetFormRequestTypes({ entityUuid: entityUuid });
  const formRequestTypes = data?.formTemplates.filter((form) => form.status === "published") || [];
  const selectedFormRequestType = formRequestTypes.find((option) => option.id === formTypeId) || null;

  const handleProceedToNewRequest = () => {
    if (formTypeId) {
      window.location.href = Routes.newRequest({
        entitySubdomain,
        formTypeId,
        baseRouterPath: pspOptions?.baseRouterPath,
      });
    }
  };

  return (
    <Dialog open={true} onClose={onClose} fullWidth data-test="newRequestDialog">
      <DialogTitle variant="h2">
        <FormattedMessage defaultMessage="Create Request" />
      </DialogTitle>
      <IconButton
        onClick={onClose}
        aria-label={intl.formatMessage({ defaultMessage: "close button for dialog" })}
        className="MuiDialog__close-button"
      >
        <Close />
      </IconButton>
      <DialogContent>
        <DialogContentText>
          <FormattedMessage defaultMessage="Select the form type you want to initiate request for." />
        </DialogContentText>
        <Autocomplete
          loading={isLoading}
          options={formRequestTypes}
          value={selectedFormRequestType}
          renderInput={(params) => (
            <TextField
              {...params}
              data-test="formTypeSelect"
              label={intl.formatMessage({ defaultMessage: "Form Type" })}
            />
          )}
          onChange={(_, value) => {
            setFormTypeId(value?.id || null);
          }}
          getOptionKey={(option) => option.id}
          getOptionLabel={(option) => option.name}
          renderOption={(props, option) => (
            <li {...props} data-test="formTypeOption">
              {option.name}
            </li>
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button data-test="cancelButton" onClick={onClose} color="secondary">
          <FormattedMessage defaultMessage="Cancel" />
        </Button>
        <Button
          data-test="confirmButton"
          color="primary"
          variant="contained"
          disabled={!formTypeId}
          onClick={handleProceedToNewRequest}
        >
          <FormattedMessage defaultMessage="Proceed" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
